/**
 * Clara Valuation Intelligence - Valuation Service (INTEGRATED VERSION)
 * 
 * Includes:
 * - Fixed NaN error handling
 * - Integrated BORIS service
 * - Calculation transparency
 * - Comprehensive error messages
 * 
 * @version 2.1.0
 * @author Manus AI
 * @date 2025-10-12
 */

import { masterSupabase as supabase } from '../lib/supabase-master.js';
// COMPARABLE-DATA-CONTRACT-1 – kanonischer Vergleichswert-Datenvertrag
import {
  adaptLegacySeedRows,
  isPlaceholderSourceUrl as contractIsPlaceholderSourceUrl,
  assessComparableProvenance as contractAssessProvenance,
  assessMarketReadiness,
  similarityScore as contractSimilarityScore
} from './comparableDataContract.js';
// INTERNAL-COMPARABLE-ADAPTER-1 – interne Faktenquelle (eigener Objekt-/Mietbestand)
import {
  adaptInternalProperties,
  buildInternalFactSummary,
  buildValueProvenanceMap
} from './adapters/internalPortfolio.js';

// Integrated BORIS Service
class BorisService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 24 * 60 * 60 * 1000;
    
    this.stateAverages = {
      'Baden-Württemberg': 450, 'Bayern': 500, 'Berlin': 800,
      'Brandenburg': 150, 'Bremen': 300, 'Hamburg': 700,
      'Hessen': 400, 'Mecklenburg-Vorpommern': 100,
      'Niedersachsen': 200, 'Nordrhein-Westfalen': 350,
      'Rheinland-Pfalz': 300, 'Saarland': 250,
      'Sachsen': 180, 'Sachsen-Anhalt': 120,
      'Schleswig-Holstein': 250, 'Thüringen': 150
    };
    
    this.cityMultipliers = {
      'München': 2.5, 'Frankfurt': 2.0, 'Stuttgart': 1.8,
      'Hamburg': 1.7, 'Berlin': 1.6, 'Düsseldorf': 1.5,
      'Köln': 1.4, 'Leipzig': 1.2, 'Dresden': 1.1, 'Nürnberg': 1.3
    };
  }

  async getBodenrichtwert({ street, houseNumber, zipCode, city, state }) {
    const cacheKey = `${zipCode}_${city}`;
    
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.data;
      }
    }

    try {
      const estimatedState = state || this.getStateFromZipCode(zipCode);
      const baseValue = this.stateAverages[estimatedState] || 300;
      const cityMultiplier = this.cityMultipliers[city] || 1.0;
      const estimatedValue = Math.round(baseValue * cityMultiplier);
      
      const result = {
        value: estimatedValue,
        zipCode,
        city,
        state: estimatedState,
        source: 'estimated',
        estimated: true,
        confidence: 40,
        method: 'state_average_with_city_adjustment',
        timestamp: new Date().toISOString()
      };
      
      this.cache.set(cacheKey, { data: result, timestamp: Date.now() });
      
      return result;
    } catch (error) {
      return {
        value: null,
        source: 'error',
        error: error.message,
        estimated: true,
        confidence: 0
      };
    }
  }

  getStateFromZipCode(zipCode) {
    if (!zipCode) return 'Unknown';
    const prefix = zipCode.substring(0, 2);
    const stateMap = {
      '01': 'Sachsen', '02': 'Sachsen', '03': 'Brandenburg', '04': 'Sachsen',
      '06': 'Sachsen-Anhalt', '07': 'Thüringen', '08': 'Baden-Württemberg',
      '09': 'Bayern', '10': 'Berlin', '12': 'Brandenburg', '13': 'Brandenburg',
      '14': 'Sachsen', '15': 'Brandenburg', '16': 'Brandenburg', '17': 'Mecklenburg-Vorpommern',
      '18': 'Mecklenburg-Vorpommern', '19': 'Mecklenburg-Vorpommern', '20': 'Hamburg',
      '21': 'Hamburg', '22': 'Hamburg', '23': 'Schleswig-Holstein', '24': 'Schleswig-Holstein',
      '25': 'Schleswig-Holstein', '26': 'Niedersachsen', '27': 'Niedersachsen',
      '28': 'Bremen', '29': 'Niedersachsen', '30': 'Niedersachsen', '31': 'Niedersachsen',
      '32': 'Niedersachsen', '33': 'Nordrhein-Westfalen', '34': 'Hessen', '35': 'Hessen',
      '36': 'Hessen', '37': 'Niedersachsen', '38': 'Sachsen-Anhalt', '39': 'Sachsen-Anhalt',
      '40': 'Nordrhein-Westfalen', '41': 'Nordrhein-Westfalen', '42': 'Nordrhein-Westfalen',
      '44': 'Nordrhein-Westfalen', '45': 'Nordrhein-Westfalen', '46': 'Nordrhein-Westfalen',
      '47': 'Nordrhein-Westfalen', '48': 'Nordrhein-Westfalen', '49': 'Nordrhein-Westfalen',
      '50': 'Nordrhein-Westfalen', '51': 'Nordrhein-Westfalen', '52': 'Nordrhein-Westfalen',
      '53': 'Nordrhein-Westfalen', '54': 'Rheinland-Pfalz', '55': 'Rheinland-Pfalz',
      '56': 'Rheinland-Pfalz', '57': 'Rheinland-Pfalz', '58': 'Nordrhein-Westfalen',
      '59': 'Nordrhein-Westfalen', '60': 'Hessen', '61': 'Hessen', '63': 'Hessen',
      '64': 'Hessen', '65': 'Hessen', '66': 'Saarland', '67': 'Rheinland-Pfalz',
      '68': 'Baden-Württemberg', '69': 'Baden-Württemberg', '70': 'Baden-Württemberg',
      '71': 'Baden-Württemberg', '72': 'Baden-Württemberg', '73': 'Baden-Württemberg',
      '74': 'Baden-Württemberg', '75': 'Baden-Württemberg', '76': 'Baden-Württemberg',
      '77': 'Baden-Württemberg', '78': 'Baden-Württemberg', '79': 'Baden-Württemberg',
      '80': 'Bayern', '81': 'Bayern', '82': 'Bayern', '83': 'Bayern', '84': 'Bayern',
      '85': 'Bayern', '86': 'Bayern', '87': 'Bayern', '88': 'Bayern', '89': 'Bayern',
      '90': 'Bayern', '91': 'Bayern', '92': 'Bayern', '93': 'Bayern', '94': 'Bayern',
      '95': 'Bayern', '96': 'Bayern', '97': 'Bayern', '98': 'Thüringen', '99': 'Thüringen'
    };
    return stateMap[prefix] || 'Unknown';
  }
}

class ValuationService {
  constructor() {
    this.borisService = new BorisService();
    
    this.defaultAssumptions = {
      opex_ratio: 0.25,
      vacancy_rate: 0.00,
      rent_growth: 0.02,
      cap_rates: {
        excellent: 0.030,
        good: 0.035,
        average: 0.045,
        poor: 0.055
      }
    };
  }

  validatePropertyData(property) {
    const errors = [];
    const warnings = [];
    
    if (!property) {
      errors.push('Keine Objektdaten vorhanden');
      return { valid: false, errors, warnings };
    }
    
    // Check for size using actual Supabase column names
    const size = property.total_area || property.total_square_meters || property.rentable_area || property.size || property.living_space;
    
    if (!size) {
      errors.push('Objektgröße fehlt (total_area, rentable_area oder total_square_meters erforderlich)');
    }
    if (size && (size <= 0 || size > 100000)) {
      errors.push(`Ungültige Objektgröße: ${size} m²`);
    }
    
    if (!property.city && !property.address) {
      errors.push('Standortdaten fehlen (city oder address erforderlich)');
    }
    
    if (!property.postal_code && !property.zip_code) {
      warnings.push('Postleitzahl fehlt - Vergleichswerte möglicherweise ungenau');
    }
    
    if (!property.build_year && !property.year_built) {
      warnings.push('Baujahr fehlt - Bewertungsgenauigkeit reduziert');
    }
    
    if (!property.property_type) {
      warnings.push('Objekttyp nicht angegeben - Standard-Annahmen werden verwendet');
    }
    
    return { valid: errors.length === 0, errors, warnings };
  }

  validateCalculationResult(value, context = '') {
    if (value === null || value === undefined) {
      throw new Error(`${context}: Berechnung lieferte keinen Wert`);
    }
    
    if (isNaN(value)) {
      throw new Error(`${context}: Berechnung lieferte ungültigen Wert (NaN)`);
    }
    
    if (!isFinite(value)) {
      throw new Error(`${context}: Berechnung lieferte unendlichen Wert`);
    }
    
    if (value <= 0) {
      throw new Error(`${context}: Berechneter Wert muss positiv sein (${value})`);
    }
    
    if (value > 100000000) {
      throw new Error(`${context}: Berechneter Wert unrealistisch hoch (${value} €)`);
    }
    
    return true;
  }

  calculateIncomeValue(property, tenants = [], assumptions = {}) {
    const calculationSteps = [];
    
    try {
      const validation = this.validatePropertyData(property);
      if (!validation.valid) {
        return {
          error: 'Unvollständige Objektdaten',
          details: validation.errors,
          warnings: validation.warnings,
          method: 'income_approach'
        };
      }
      
      const config = { ...this.defaultAssumptions, ...assumptions };
      
      if (!tenants || tenants.length === 0) {
        return {
          error: 'Keine Mietdaten verfügbar',
          message: 'Das Ertragswertverfahren benötigt Mieteinnahmen zur Berechnung.',
          recommendation: 'Bitte fügen Sie Mieter mit Mietdaten hinzu oder nutzen Sie nur das Vergleichswertverfahren.',
          method: 'income_approach',
          warnings: validation.warnings
        };
      }
      
      let annualGrossRent = 0;
      let rentCount = 0;
      
      tenants.forEach(tenant => {
        const rent = parseFloat(tenant.cold_rent || tenant.monthly_rent || 0);
        if (rent > 0) {
          annualGrossRent += rent;
          rentCount++;
        }
      });
      
      annualGrossRent *= 12;
      
      calculationSteps.push({
        step: 1,
        name: 'Jahresbruttomiete',
        formula: `${rentCount} Mieter × Durchschnitt ${Math.round(annualGrossRent / rentCount / 12)}€ × 12 Monate`,
        result: annualGrossRent,
        unit: '€'
      });
      
      if (annualGrossRent === 0) {
        return {
          error: 'Keine gültigen Mietdaten',
          message: 'Alle Mieter haben Miete = 0 oder ungültige Werte.',
          recommendation: 'Bitte prüfen Sie die Mietdaten und korrigieren Sie diese.',
          method: 'income_approach',
          warnings: validation.warnings
        };
      }

      const vacancyAdjustment = annualGrossRent * config.vacancy_rate;
      const annualEffectiveRent = annualGrossRent * (1 - config.vacancy_rate);
      
      calculationSteps.push({
        step: 2,
        name: 'Leerstandsabschlag',
        formula: `${Math.round(annualGrossRent)}€ × ${(config.vacancy_rate * 100).toFixed(1)}%`,
        result: vacancyAdjustment,
        unit: '€'
      });

      const opex = annualEffectiveRent * config.opex_ratio;
      
      calculationSteps.push({
        step: 3,
        name: 'Bewirtschaftungskosten',
        formula: `${Math.round(annualEffectiveRent)}€ × ${(config.opex_ratio * 100).toFixed(0)}%`,
        result: opex,
        unit: '€'
      });

      const noi = annualEffectiveRent - opex;
      
      calculationSteps.push({
        step: 4,
        name: 'Nettobetriebsertrag (NOI)',
        formula: `${Math.round(annualEffectiveRent)}€ - ${Math.round(opex)}€`,
        result: noi,
        unit: '€'
      });
      
      if (noi <= 0) {
        return {
          error: 'Negativer Nettobetriebsertrag',
          message: `Die Bewirtschaftungskosten (${Math.round(opex)}€) übersteigen die Mieteinnahmen (${Math.round(annualEffectiveRent)}€).`,
          recommendation: 'Bitte prüfen Sie die Mietdaten oder passen Sie die Bewirtschaftungskosten an.',
          method: 'income_approach',
          calculation_steps: calculationSteps,
          warnings: validation.warnings
        };
      }

      const locationScore = property.location_score || 50;
      let capRate;
      let capRateCategory;
      
      if (locationScore >= 80) {
        capRate = config.cap_rates.excellent;
        capRateCategory = 'excellent';
      } else if (locationScore >= 60) {
        capRate = config.cap_rates.good;
        capRateCategory = 'good';
      } else if (locationScore >= 40) {
        capRate = config.cap_rates.average;
        capRateCategory = 'average';
      } else {
        capRate = config.cap_rates.poor;
        capRateCategory = 'poor';
      }
      
      const capRateLabels = {
        excellent: 'Exzellent (Top-Lage)',
        good: 'Gut',
        average: 'Durchschnittlich',
        poor: 'Schwach'
      };
      
      calculationSteps.push({
        step: 5,
        name: 'Kapitalisierungszinssatz',
        formula: `Lage "${capRateLabels[capRateCategory]}" (Score: ${locationScore}) → ${(capRate * 100).toFixed(1)}%`,
        result: capRate,
        unit: '%',
        display: (capRate * 100).toFixed(1) + '%'
      });

      const incomeValue = noi / capRate;
      
      calculationSteps.push({
        step: 6,
        name: 'Ertragswert',
        formula: `${Math.round(noi)}€ / ${(capRate * 100).toFixed(1)}%`,
        result: incomeValue,
        unit: '€'
      });
      
      try {
        this.validateCalculationResult(incomeValue, 'Ertragswert');
      } catch (validationError) {
        return {
          error: validationError.message,
          debug: { annualGrossRent, annualEffectiveRent, opex, noi, capRate, incomeValue },
          calculation_steps: calculationSteps,
          method: 'income_approach',
          warnings: validation.warnings
        };
      }

      return {
        value: Math.round(incomeValue),
        method: 'income_approach',
        details: {
          annual_gross_rent: Math.round(annualGrossRent),
          vacancy_adjustment: Math.round(vacancyAdjustment),
          annual_effective_rent: Math.round(annualEffectiveRent),
          operating_expenses: Math.round(opex),
          net_operating_income: Math.round(noi),
          cap_rate: capRate,
          cap_rate_category: capRateCategory,
          location_score: locationScore,
          tenant_count: tenants.length,
          rent_count: rentCount
        },
        calculation_steps: calculationSteps,
        data_sources: ['tenant_rent_data', 'location_score'],
        warnings: validation.warnings
      };
    } catch (error) {
      console.error('[ValuationService] Error calculating income value:', error);
      return {
        error: 'Unerwarteter Fehler bei der Berechnung',
        message: error.message,
        method: 'income_approach',
        calculation_steps: calculationSteps
      };
    }
  }

  // SEED-COMPARABLE-NEUTRALIZATION-1 / COMPARABLE-DATA-CONTRACT-1 (Fundament)
  // Eine Zahl darf nur dann "Marktwert" heißen, wenn ihre Herkunft belegbar ist.
  // Seed-/Demo-Zeilen bleiben im System erhalten (Audit-Trail), sind aber für
  // jede Wertermittlung und jede Anzeige als Marktdatum gesperrt.
  // COMPARABLE-DATA-CONTRACT-1: Herkunftssperre lebt im Vertragsmodul,
  // hier bleiben nur schlanke Delegationen für bestehende Aufrufer.
  isPlaceholderSourceUrl(url) {
    return contractIsPlaceholderSourceUrl(url);
  }
  assessComparableProvenance(comparables = []) {
    return contractAssessProvenance(comparables);
  }
  calculateComparableValue(property, comparables = [], weights = {}) {
    const calculationSteps = [];
    
    try {
      const validation = this.validatePropertyData(property);
      if (!validation.valid) {
        return {
          error: 'Unvollständige Objektdaten',
          details: validation.errors,
          warnings: validation.warnings,
          method: 'comparable_sales'
        };
      }
      
      if (!comparables || comparables.length === 0) {
        return {
          error: 'Keine Vergleichsobjekte verfügbar',
          message: 'Das Vergleichswertverfahren benötigt mindestens ein Vergleichsobjekt.',
          recommendation: 'Bitte laden Sie Vergleichsobjekte oder nutzen Sie nur das Ertragswertverfahren.',
          method: 'comparable_sales',
          warnings: validation.warnings
        };
      }

      // Herkunftssperre: nur nachweisbar reale Marktdaten duerfen rechnen
      const provenance = this.assessComparableProvenance(comparables);
      const verifiedComparables = provenance.filter(p => p.verified).map(p => p.comp);
      const rejectedCount = provenance.length - verifiedComparables.length;
      if (rejectedCount > 0) {
        calculationSteps.push({
          step: 0,
          name: 'Herkunftspruefung',
          formula: rejectedCount + ' von ' + provenance.length + ' Datensaetzen nicht als Marktdatum belegbar',
          result: verifiedComparables.length,
          unit: 'belegbare Objekte'
        });
      }
      if (verifiedComparables.length === 0) {
        return {
          error: 'Keine belastbare Vergleichsbasis',
          provenance_gap: true,
          message: 'Die vorliegenden Vergleichsdaten sind nicht als reale Marktdaten belegbar (Demo-/Musterwerte oder fehlende Herkunft).',
          recommendation: 'Reale Markt- oder Transaktionsdaten anbinden, bevor der Marktvergleich aktiviert wird.',
          method: 'comparable_sales',
          rejected: { total: provenance.length, rejected: rejectedCount },
          warnings: validation.warnings
        };
      }
      const propertyCity = property.city || property.address?.split(',')[1]?.trim();
      const localComparables = verifiedComparables.filter(comp => {
        // Skip null/undefined comparables
        if (!comp || typeof comp !== 'object') return false;
        
        const compCity = comp.city || comp.address?.split(',')[1]?.trim();
        return compCity && propertyCity && compCity.toLowerCase() === propertyCity.toLowerCase();
      });

      const useComparables = (localComparables.length > 0 ? localComparables : verifiedComparables)
        .filter(comp => comp && typeof comp === 'object');
      
      if (useComparables.length === 0) {
        return {
          error: 'Keine passenden Vergleichsobjekte',
          message: `Keine Vergleichsobjekte in ${propertyCity} gefunden.`,
          recommendation: 'Erweitern Sie den Suchradius oder nutzen Sie das Ertragswertverfahren.',
          method: 'comparable_sales',
          warnings: validation.warnings
        };
      }

      calculationSteps.push({
        step: 1,
        name: 'Vergleichsobjekte gefiltert',
        formula: `${verifiedComparables.length} belegbar → ${useComparables.length} in ${propertyCity || 'Umgebung'}`,
        result: useComparables.length,
        unit: 'Objekte'
      });

      let totalWeightedPrice = 0;
      let totalWeight = 0;
      const comparableDetails = [];
      let validComparables = 0;

      useComparables.forEach((comp, index) => {
        try {
          // Skip if comp is null or undefined
          if (!comp || typeof comp !== 'object') {
            console.warn(`[Valuation] Skipping invalid comparable at index ${index}`);
            return;
          }
          
          const compId = comp.id || index;
          const weight = weights[compId] !== undefined ? weights[compId] / 100 : 1.0;
          
          const compPrice = parseFloat(comp.price || 0);
          const compSize = parseFloat(comp.living_space || comp.livingSpace || comp.size || 0);
          
          if (compPrice <= 0 || compSize <= 0) {
            return;
          }
          
          const pricePerSqm = compPrice / compSize;
          
          if (isNaN(pricePerSqm) || !isFinite(pricePerSqm) || pricePerSqm <= 0) {
            return;
          }
          
          totalWeightedPrice += pricePerSqm * weight;
          totalWeight += weight;
          validComparables++;

          comparableDetails.push({
            id: compId,
            address: comp.address,
            price_per_sqm: Math.round(pricePerSqm),
            size: compSize,
            total_price: compPrice,
            weight: weight,
            similarity_score: this.calculateSimilarityScore(property, comp)
          });
        } catch (error) {
          console.error(`[Valuation] Error processing comparable:`, error);
        }
      });
      
      if (validComparables === 0 || totalWeight === 0) {
        return {
          error: 'Keine gültigen Vergleichsobjekte',
          message: 'Alle Vergleichsobjekte haben ungültige Preis- oder Größenangaben.',
          recommendation: 'Bitte prüfen Sie die Vergleichsdaten oder nutzen Sie das Ertragswertverfahren.',
          method: 'comparable_sales',
          warnings: validation.warnings
        };
      }

      const avgPricePerSqm = totalWeightedPrice / totalWeight;
      
      calculationSteps.push({
        step: 2,
        name: 'Durchschnittlicher Quadratmeterpreis',
        formula: `Gewichteter Durchschnitt aus ${validComparables} Objekten`,
        result: avgPricePerSqm,
        unit: '€/m²'
      });

      const locationAdjustment = this.calculateLocationAdjustment(property);
      const adjustedPricePerSqm = avgPricePerSqm * (1 + locationAdjustment);
      
      calculationSteps.push({
        step: 3,
        name: 'Lageanpassung',
        formula: `${Math.round(avgPricePerSqm)}€/m² × ${(1 + locationAdjustment).toFixed(3)} (${locationAdjustment > 0 ? '+' : ''}${(locationAdjustment * 100).toFixed(1)}%)`,
        result: adjustedPricePerSqm,
        unit: '€/m²'
      });

      const propertySize = property.total_area || property.total_square_meters || property.rentable_area || property.size || property.living_space;
      const comparableValue = adjustedPricePerSqm * propertySize;
      
      calculationSteps.push({
        step: 4,
        name: 'Vergleichswert',
        formula: `${Math.round(adjustedPricePerSqm)}€/m² × ${propertySize}m²`,
        result: comparableValue,
        unit: '€'
      });
      
      try {
        this.validateCalculationResult(comparableValue, 'Vergleichswert');
      } catch (validationError) {
        return {
          error: validationError.message,
          debug: { avgPricePerSqm, locationAdjustment, adjustedPricePerSqm, propertySize, comparableValue },
          calculation_steps: calculationSteps,
          method: 'comparable_sales',
          warnings: validation.warnings
        };
      }

      return {
        value: Math.round(comparableValue),
        method: 'comparable_sales',
        details: {
          comparables_count: validComparables,
          total_comparables: useComparables.length,
          verified_comparables: verifiedComparables.length,
          rejected_comparables: rejectedCount,
          avg_price_per_sqm: Math.round(avgPricePerSqm),
          location_adjustment: locationAdjustment,
          adjusted_price_per_sqm: Math.round(adjustedPricePerSqm),
          property_size: propertySize,
          comparables: comparableDetails
        },
        calculation_steps: calculationSteps,
        data_sources: ['property_suggestions'],
        warnings: validation.warnings
      };
    } catch (error) {
      console.error('[ValuationService] Error calculating comparable value:', error);
      return {
        error: 'Unerwarteter Fehler bei der Berechnung',
        message: error.message,
        method: 'comparable_sales',
        calculation_steps: calculationSteps
      };
    }
  }

  async calculateFairValueBand(property, tenants = [], comparables = [], options = {}) {
    // Declare these outside try block so they're available in catch
    let incomeResult = null;
    let comparableResult = null;
    
    try {
      const validation = this.validatePropertyData(property);
      if (!validation.valid) {
        throw new Error(`Objektdaten unvollständig: ${validation.errors.join(', ')}`);
      }
      
      const {
        assumptions = {},
        weights = {},
        income_weight = 0.4,
        comparable_weight = 0.6
      } = options;
      // COMPARABLE-DATA-CONTRACT-1: alle Quellen auf das kanonische Schema
      const canonicalComparables = adaptLegacySeedRows(comparables);
      const marketReadiness = assessMarketReadiness(property, canonicalComparables);
      // INTERNAL-COMPARABLE-ADAPTER-1: interne Faktenquelle (read-only, kein Marktvergleich)
      const internalComparables = adaptInternalProperties(options.internalRows || []);
      const internalFacts = buildInternalFactSummary(internalComparables, {
        subject: property,
        rentLayer: options.rentLayer || null
      });

      // Fetch BORIS land value data
      let borisData = null;
      try {
        borisData = await this.borisService.getBodenrichtwert({
          street: property.street || property.address,
          houseNumber: property.house_number,
          zipCode: property.postal_code || property.zip_code,
          city: property.city,
          state: property.state
        });
        console.log('[Valuation] BORIS land value:', borisData);
      } catch (error) {
        console.warn('[Valuation] BORIS fetch failed:', error.message);
      }

      console.log('[Valuation] Calculating income value...');
      incomeResult = this.calculateIncomeValue(property, tenants, assumptions);
      
      console.log('[Valuation] Calculating comparable value...');
      comparableResult = this.calculateComparableValue(property, canonicalComparables, weights);
      if (comparableResult && typeof comparableResult === 'object') {
        comparableResult.market_readiness = marketReadiness;
      }

      const allErrors = [];
      const allWarnings = [...(validation.warnings || [])];
      
      if (incomeResult?.error) {
        allErrors.push(`Ertragswert: ${incomeResult.error}`);
        if (incomeResult.warnings) allWarnings.push(...incomeResult.warnings);
      }
      
      if (comparableResult?.error) {
        // Fehlende Marktdaten sind eine Datenluecke, kein Berechnungsfehler
        const bucket = comparableResult.provenance_gap ? allWarnings : allErrors;
        bucket.push(`Vergleichswert: ${comparableResult.error}`);
        if (comparableResult.warnings) allWarnings.push(...comparableResult.warnings);
      }

      let baseValue;
      let methodsUsed = [];
      let confidence = 50;
      let valuationMethod = '';

      if (incomeResult && !incomeResult.error && comparableResult && !comparableResult.error) {
        baseValue = (incomeResult.value * income_weight) + (comparableResult.value * comparable_weight);
        methodsUsed = ['income_approach', 'comparable_sales'];
        valuationMethod = 'combined';
        confidence = 85;
      } else if (incomeResult && !incomeResult.error) {
        baseValue = incomeResult.value;
        methodsUsed = ['income_approach'];
        valuationMethod = 'income_only';
        confidence = 65;
      } else if (comparableResult && !comparableResult.error) {
        baseValue = comparableResult.value;
        methodsUsed = ['comparable_sales'];
        valuationMethod = 'comparable_only';
        confidence = 70;
      } else {
        throw new Error(
          'Bewertung nicht möglich. ' + 
          (allErrors.length > 0 ? allErrors.join(' | ') : 'Keine Berechnungsmethode verfügbar.')
        );
      }
      
      this.validateCalculationResult(baseValue, 'Basiswert');

      const confidenceDetails = this.calculateConfidence(property, tenants, canonicalComparables, {
        incomeResult,
        comparableResult
      });

      const conservativeValue = Math.round(baseValue * 0.9);
      const aggressiveValue = Math.round(baseValue * 1.1);

      return {
        success: true,
        fair_value_band: {
          conservative: conservativeValue,
          base: Math.round(baseValue),
          aggressive: aggressiveValue
        },
        confidence: confidenceDetails.score,
        confidence_details: confidenceDetails,
        methods: {
          income_approach: incomeResult,
          comparable_sales: comparableResult
        },
        boris_land_value: borisData,
        market_readiness: marketReadiness,
        internal_facts: internalFacts,
        value_provenance: buildValueProvenanceMap({
          internalFacts,
          comparablesCount: canonicalComparables.length
        }),
        weights: valuationMethod === 'combined'
          ? { income: income_weight, comparable: comparable_weight }
          : valuationMethod === 'income_only'
            ? { income: 1, comparable: 0 }
            : { income: 0, comparable: 1 },
        valuation_method: valuationMethod,
        data_lineage: {
          timestamp: new Date().toISOString(),
          sources: this.getDataSources(incomeResult, comparableResult, borisData),
          methods_used: methodsUsed,
          property_id: property.id,
          property_name: property.property_name || property.address
        },
        errors: allErrors,
        warnings: allWarnings
      };
    } catch (error) {
      console.error('[ValuationService] Error calculating fair value band:', error);
      
      return {
        success: false,
        error: error.message,
        fair_value_band: null,
        confidence: 0,
        confidence_details: {
          score: 0,
          factors: [],
          recommendations: ['Fehler bei der Berechnung aufgetreten']
        },
        methods: {
          income_approach: incomeResult || { error: 'Nicht berechnet' },
          comparable_sales: comparableResult || { error: 'Nicht berechnet' }
        },
        data_lineage: {
          timestamp: new Date().toISOString(),
          methods_used: [],
          sources: [],
          error: error.message,
          property_id: property.id,
          property_name: property.property_name || property.address
        },
        errors: [error.message],
        warnings: []
      };
    }
  }

  calculateConfidence(property, tenants, comparables, results) {
    let score = 50;
    const factors = [];

    if (property.address && property.city && (property.postal_code || property.zip_code)) {
      score += 5;
      factors.push({ factor: 'address_complete', impact: 5, status: 'positive' });
    }
    if (property.total_area || property.total_square_meters || property.rentable_area || property.size || property.living_space) {
      score += 5;
      factors.push({ factor: 'size_available', impact: 5, status: 'positive' });
    }
    if (property.year_built || property.construction_year || property.build_year) {
      score += 5;
      factors.push({ factor: 'build_year_available', impact: 5, status: 'positive' });
    }
    if (property.property_type) {
      score += 5;
      factors.push({ factor: 'property_type_available', impact: 5, status: 'positive' });
    }

    if (tenants && tenants.length > 0) {
      score += 10;
      factors.push({ factor: 'tenant_data_available', impact: 10, status: 'positive' });
      const validRents = tenants.filter(t => (t.cold_rent || t.monthly_rent) > 0).length;
      if (validRents === tenants.length) {
        score += 5;
        factors.push({ factor: 'all_rents_available', impact: 5, status: 'positive' });
      } else if (validRents > 0) {
        score += 2;
        factors.push({ factor: 'partial_rents_available', impact: 2, status: 'warning' });
      }
    } else {
      factors.push({ factor: 'no_tenant_data', impact: -10, status: 'negative' });
    }

    // Nur belegbare Marktdaten duerfen die Datenbasis erhöhen
    const verifiedComparableCount = this.assessComparableProvenance(comparables || [])
      .filter(p => p.verified).length;
    if (verifiedComparableCount >= 5) {
      score += 15;
      factors.push({ factor: 'sufficient_comparables', impact: 15, status: 'positive' });
    } else if (verifiedComparableCount >= 3) {
      score += 10;
      factors.push({ factor: 'adequate_comparables', impact: 10, status: 'positive' });
    } else if (verifiedComparableCount > 0) {
      score += 5;
      factors.push({ factor: 'few_comparables', impact: 5, status: 'warning' });
    } else {
      score -= 10;
      factors.push({ factor: 'no_comparables', impact: -10, status: 'negative' });
    }

    if (results.incomeResult && !results.incomeResult.error && 
        results.comparableResult && !results.comparableResult.error) {
      score += 10;
      factors.push({ factor: 'multiple_methods', impact: 10, status: 'positive' });
    } else {
      score -= 5;
      factors.push({ factor: 'single_method_only', impact: -5, status: 'warning' });
    }

    score = Math.max(0, Math.min(score, 100));

    return {
      score: Math.round(score),
      factors: factors,
      recommendations: this.getConfidenceRecommendations(factors)
    };
  }

  getConfidenceRecommendations(factors) {
    const recommendations = [];
    
    const negativeFactors = factors.filter(f => f.status === 'negative' || f.status === 'warning');
    
    negativeFactors.forEach(factor => {
      switch (factor.factor) {
        case 'no_tenant_data':
          recommendations.push('Mietdaten hinzufügen für präzisere Ertragswertberechnung');
          break;
        case 'partial_rents_available':
          recommendations.push('Fehlende Mietdaten vervollständigen');
          break;
        case 'no_comparables':
          recommendations.push('Suchradius erweitern oder Objekttyp anpassen für mehr Vergleichsobjekte');
          break;
        case 'few_comparables':
          recommendations.push('Mehr Vergleichsobjekte laden für höhere Genauigkeit');
          break;
        case 'single_method_only':
          recommendations.push('Beide Bewertungsmethoden nutzen für höhere Zuverlässigkeit');
          break;
      }
    });

    if (recommendations.length === 0) {
      recommendations.push('Bewertung basiert auf vollständigen Daten - keine Verbesserungen nötig');
    }

    return recommendations;
  }

  calculateSimilarityScore(property, comparable) {
    return contractSimilarityScore(property, comparable);
  }
  calculateLocationAdjustment(property) {
    const locationScore = property.location_score || 50;
    return ((locationScore - 50) / 50) * 0.2;
  }

  getCapRateCategory(locationScore) {
    if (locationScore >= 80) return 'excellent';
    if (locationScore >= 60) return 'good';
    if (locationScore >= 40) return 'average';
    return 'poor';
  }

  getDataSources(incomeResult, comparableResult, borisData) {
    const sources = [];
    
    if (incomeResult && !incomeResult.error) {
      sources.push({
        name: 'tenant_rent_data',
        status: 'available',
        count: incomeResult.details?.tenant_count || 0
      });
      sources.push({
        name: 'location_score',
        status: 'available',
        value: incomeResult.details?.location_score || 50
      });
    } else if (incomeResult?.error) {
      sources.push({
        name: 'tenant_rent_data',
        status: 'unavailable',
        reason: incomeResult.error
      });
    }
    
    if (comparableResult && !comparableResult.error) {
      sources.push({
        name: 'property_suggestions',
        status: 'available',
        count: comparableResult.details?.comparables_count || 0
      });
    } else if (comparableResult?.error) {
      sources.push({
        name: 'property_suggestions',
        status: 'unavailable',
        reason: comparableResult.error
      });
    }
    
    if (borisData && borisData.value) {
      sources.push({
        name: 'boris_land_value',
        status: 'available',
        value: borisData.value,
        confidence: borisData.confidence
      });
    } else {
      sources.push({
        name: 'boris_land_value',
        status: 'unavailable',
        reason: borisData?.error || 'Nicht abgerufen'
      });
    }
    
    return sources;
  }

  async saveValuation(propertyId, valuation, userId) {
    try {
      try {
        const { data, error } = await supabase
          .from('valuation_history')
          .insert({
            property_id: propertyId,
            valuation_date: new Date().toISOString(),
            conservative_value: valuation.fair_value_band?.conservative,
            base_value: valuation.fair_value_band?.base,
            aggressive_value: valuation.fair_value_band?.aggressive,
            confidence_score: valuation.confidence,
            method: valuation.data_lineage?.methods_used?.join(','),
            details: valuation,
            created_by: userId
          })
          .select()
          .single();

        if (error) throw error;

        console.log('[ValuationService] Valuation saved to history:', data.id);
        return data;
      } catch (historyError) {
        console.warn('[ValuationService] valuation_history table not available, using fallback');
        
        const { data, error } = await supabase
          .from('properties')
          .update({
            latest_valuation: valuation,
            latest_valuation_date: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', propertyId)
          .select()
          .single();

        if (error) throw error;

        console.log('[ValuationService] Valuation saved to property record');
        return data;
      }
    } catch (error) {
      console.error('[ValuationService] Error saving valuation:', error);
      throw error;
    }
  }

  async getValuationHistory(propertyId) {
    try {
      const { data, error } = await supabase
        .from('valuation_history')
        .select('*')
        .eq('property_id', propertyId)
        .order('valuation_date', { ascending: false });

      if (error) {
        const { data: propData, error: propError } = await supabase
          .from('properties')
          .select('latest_valuation, latest_valuation_date')
          .eq('id', propertyId)
          .single();

        if (propError) throw propError;

        if (propData?.latest_valuation) {
          return [{
            valuation_date: propData.latest_valuation_date,
            ...propData.latest_valuation
          }];
        }

        return [];
      }

      return data || [];
    } catch (error) {
      console.error('[ValuationService] Error fetching valuation history:', error);
      return [];
    }
  }
}

const valuationService = new ValuationService();
export default valuationService;

